import { createSlice } from '@reduxjs/toolkit';
import { LearningPathwayState, LearningPathwayStateData } from 'src/types/state/learningPathway';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { suApiSlice } from './api/su';
import { LearningPathwayStaticApiData } from '../../types/api/learningPathwayStatic';
import type { AppState } from '../store';

// State of the Learning Pathways Slice
export interface LearningPathwaysSliceState {
  byId: Record<string, LearningPathwayState>
  // Store for additional learning pathway metadata
}

export const initialState: LearningPathwaysSliceState = {
  byId: {},
};

export const learningPathwaysSlice = createSlice({
  name: 'learningPathways',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Helper function to process learning pathway data into state
    const processLearningPathwayData = (
      state: LearningPathwaysSliceState,
      learningPathwayData: LearningPathwayStaticApiData,
      options: { preserveContent?: boolean } = {},
    ) => {
      try {
        if (!learningPathwayData || !learningPathwayData.learningPathwayId) {
          ClientLogger.error(ClientLog.IllegalInternalDataState, {
            payload: learningPathwayData,
            url: window.location.href,
          });
          return;
        }

        const processedSections = learningPathwayData.sections?.map((section) => ({
          ...section,
          learningModuleIds: section.moduleList?.map((module) => module.moduleId) || [],
        }));

        const existingData = state.byId[learningPathwayData.learningPathwayId]?.data;

        // Metadata-only responses omit sections and carry only partial stats (e.g. just
        // totalDuration). When asked to preserve, keep any sections already cached from a
        // full fetch, and merge stats so cached counts (totalSections/totalModules/etc.)
        // are not lost; otherwise fall back to the incoming values (existing behavior).
        const sections = processedSections
          ?? (options.preserveContent ? existingData?.sections : undefined)
          ?? [];

        const learningPathwayStats = options.preserveContent
          ? { ...existingData?.learningPathwayStats, ...learningPathwayData.learningPathwayStats }
          : learningPathwayData.learningPathwayStats;

        const stateData: LearningPathwayStateData = {
          ...learningPathwayData,
          imageSrc: learningPathwayData.backgroundImage,
          sections,
          learningPathwayStats,
        };

        state.byId[learningPathwayData.learningPathwayId] = { data: stateData };
      } catch (error) {
        ClientLogger.error(ClientLog.PayloadProcessingError, {
          learningPathwayId: learningPathwayData.learningPathwayId,
          url: window.location.href,
        });
      }
    };

    builder
      .addMatcher(
        suApiSlice.endpoints.getLearningPathway.matchFulfilled,
        (state, action) => {
          // Metadata-only responses omit sections and carry only partial stats; preserve
          // already-cached sections and merge stats so the LP page keeps its modules AND
          // its section/module counts, while still populating byId so the topic banner
          // renders on a cold cache.
          processLearningPathwayData(state, action.payload.learningPathway, {
            preserveContent: action.meta.arg.originalArgs?.metadataOnly,
          });
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getCustomerCredential.matchFulfilled,
        (state, action) => {
          try {
            processLearningPathwayData(state, action.payload.customerCredential.credential.learningPathway);
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getLearningPathwayStatic.matchFulfilled,
        (state, action) => {
          // Process learning pathway data into our store from the GET LearningPathwayStatic API
          const getLearningPathwaysById = (learningPathwayData: LearningPathwayStaticApiData) => {
            try {
              const learningPathwaysById: Record<string, LearningPathwayState> = {};

              // Validate required fields
              if (!learningPathwayData || !learningPathwayData.learningPathwayId) {
                ClientLogger.error(ClientLog.IllegalInternalDataState, {
                  payload: learningPathwayData,
                  url: window.location.href,
                });
                return learningPathwaysById;
              }

              const stateData: LearningPathwayStateData = {
                ...learningPathwayData,
                imageSrc: learningPathwayData.backgroundImage,
                sections: learningPathwayData.sections?.map((section) => {
                  const learningModuleIds = section.moduleList
                    ?.map((module) => module.moduleId) || [];

                  return {
                    ...section,
                    learningModuleIds,
                  };
                }) || [],
              };

              // Store the pathway data
              learningPathwaysById[learningPathwayData.learningPathwayId] = {
                data: stateData,
              };

              return learningPathwaysById;
            } catch (error) {
              ClientLogger.error(ClientLog.PayloadProcessingError, {
                learningPathwayId: learningPathwayData.learningPathwayId,
                url: window.location.href,
              });
              return {};
            }
          };

          state.byId = {
            ...state.byId,
            ...getLearningPathwaysById(action.payload.learningPathway),
          };
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listLearningPathways.matchFulfilled,
        (state, action) => {
          // Process learning pathways list data into our store
          try {
            action.payload.learningPathways.forEach((pathwayData: LearningPathwayStaticApiData) => {
              processLearningPathwayData(state, pathwayData);
            });
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listLearningPathwaysPoly.matchFulfilled,
        (state, action) => {
          // Process PolyD learning pathways list data into our store
          try {
            action.payload.learningPathways.forEach((pathwayData: LearningPathwayStaticApiData) => {
              processLearningPathwayData(state, pathwayData);
            });
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, { url: window.location.href });
          }
        },
      );
    // Add more matchers that may react to another slice's actions/APIs
  },
});

// Basic selectors
export const learningPathwaysActions = learningPathwaysSlice.actions;
export const selectLearningPathwayById = (
  state: AppState,
  learningPathwayId: string,
) => {
  return state.learningPathways.byId[learningPathwayId];
};

// Selectors for learning pathways list
export const selectAllLearningPathways = (state: AppState) => {
  return Object.values(state.learningPathways.byId);
};
